import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Wallet, Target, Sparkles, Check, Crown, TrendingUp, LineChart, ShieldCheck, Activity } from "lucide-react";
import SEO from "../components/SEO";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-black">
      <SEO
        title="Personal Finance Coach with AI"
        description="RED.FLAG tracks your expenses, sets category budgets, and turns red the moment you overspend. Score your financial wellness out of 100, track a live stress indicator, hit savings goals, and get an AI coach that reads the custom data you provide. ₹200 one-time payment for lifetime premium access."
        path="/"
      />

      {/* NAV */}
      <header className="border-b-2 border-black bg-white">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <div className="w-7 h-7 bg-[var(--alert-red)] flex items-center justify-center text-white font-display font-black text-sm">!</div>
            <div className="font-display font-black text-xl tracking-tighter">RED.FLAG</div>
            <div className="overline hidden sm:block">PERSONAL FINANCE COACH</div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin"
              className="border-2 border-black px-4 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors">
              SIGN IN
            </Link>
            <Link to="/register" data-testid="landing-getstarted"
              className="bg-black text-white px-4 py-2 font-display font-bold text-sm hover:bg-[var(--alert-red)] transition-colors">
              GET STARTED →
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b-2 border-black bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="overline text-white/60 mb-4">TRACK · BUDGET · COACH</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
              Spend smart.<br />
              <span className="text-[var(--alert-red)]">Get flagged</span> early.
            </h1>
            <p className="mt-8 font-mono text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              Most people don&apos;t know they&apos;ve blown their monthly budget until the credit card bill lands.
              RED.FLAG tracks every rupee, scores your financial wellness out of 100, surfaces a live stress indicator,
              and has an AI Coach that reads the custom data you provide to tell you exactly why your score moved.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta"
                className="bg-[var(--alert-red)] text-white px-8 py-4 font-display font-bold tracking-wider hover:bg-white hover:text-black transition-colors">
                START FREE →
              </Link>
              <a href="#pricing"
                className="border-2 border-white text-white px-8 py-4 font-display font-bold tracking-wider hover:bg-white hover:text-black transition-colors">
                SEE PRICING
              </a>
            </div>
            <div className="mt-8 font-mono text-xs text-white/40">
              No credit card. Free forever tier. Special launch price · ₹200 one-time.
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-2">
            <div className="bg-[var(--alert-wash)] border-2 border-[var(--alert-red)] p-6 flag-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-[var(--alert-red)] text-white font-display font-black flex items-center justify-center">!</div>
                <div className="overline text-[var(--alert-red)]">RED FLAG / 3 ALERTS</div>
              </div>
              <ul className="font-mono text-sm text-[var(--alert-red)] space-y-1">
                <li>— Food budget exceeded by ₹847</li>
                <li>— Entertainment 132% of limit</li>
                <li>— Overall budget breached this month</li>
              </ul>
            </div>
            <div className="mt-4 bg-white border-2 border-black p-6">
              <div className="overline mb-2 flex items-center gap-2 text-black">
                <Sparkles size={12} /> AI COACH
              </div>
              <div className="font-mono text-sm leading-relaxed">
                <span className="text-[var(--alert-red)] font-bold">█</span>{" "}
                You&apos;re 67% over food budget — mostly weekend Zomato.
                Cap dining out at ₹2,000 this week and you&apos;ll claw back ₹1,800.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b-2 border-black bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <div className="overline mb-3">THE PROBLEM</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter max-w-3xl mx-auto">
            Spreadsheets don&apos;t <span className="text-[var(--alert-red)]">yell</span> at you.
          </h2>
          <p className="mt-6 font-mono text-base text-[var(--text-secondary)] max-w-2xl mx-auto leading-relaxed">
            Traditional trackers show numbers. Numbers don&apos;t stop you from swiping. RED.FLAG turns your dashboard red,
            pushes alerts, and gives you an AI Coach that names exactly which category is bleeding.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b-2 border-black bg-[var(--bg-page)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <div className="overline mb-3">EVERYTHING YOU NEED</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
              Built for people who actually want to save.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: AlertTriangle, title: "Red Flag Alerts", body: "Set overall and per-category budgets. The dashboard turns red the second you cross the line — no waiting for the bill." },
              { icon: ShieldCheck, title: "Financial Wellness Score", body: "A 0–100 score composed of Budget health (40%), Goal progress (35%) and Savings resilience (25%). See exactly what's dragging your number down." },
              { icon: Activity, title: "Financial Stress Indicator", body: "A live 7-factor stress percentage — overspend, savings rate, emergency fund, bills, goals, volatility, discretionary ratio — with month-over-month trend arrow." },
              { icon: Wallet, title: "Salary Planner", body: "Log monthly income. Get instant 50/30/20 allocation suggestions. See your true savings rate every month." },
              { icon: Target, title: "Savings Goals", body: "Create goals with deadlines. RED.FLAG calculates the exact monthly contribution needed to hit them on time." },
              { icon: Sparkles, title: "AI Financial Coach", body: "Chat with an AI coach powered by Claude Sonnet 4.5. It reads the custom data you provide — income, budgets, expenses, goals — and gives actionable advice." },
              { icon: LineChart, title: "Charts & Insights", body: "Category bar charts, daily spend trends, monthly AI verdicts. See where your money actually goes." },
              { icon: TrendingUp, title: "Multi-Currency + CSV Export", body: "Track in ₹, $, €, £ and more. Export your entire expense history to CSV for taxes or spreadsheet analysis." },
            ].map((f) => (
              <div key={f.title} className="bg-white border-2 border-black p-6 brutal-hover">
                <div className="w-10 h-10 bg-black text-white flex items-center justify-center mb-4">
                  <f.icon size={20} />
                </div>
                <h3 className="font-display font-black text-xl tracking-tight mb-2">{f.title}</h3>
                <p className="font-mono text-sm text-[var(--text-secondary)] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b-2 border-black bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <div className="overline text-white/60 mb-3">NO SUBSCRIPTIONS</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">One payment. Lifetime access.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white text-black p-8 border-2 border-white">
              <div className="overline">FREE FOREVER</div>
              <div className="font-display font-black text-6xl tracking-tighter mt-3">₹0</div>
              <ul className="mt-6 space-y-2 font-mono text-sm">
                {["Expense tracking", "Category & overall budgets", "Red flag alerts", "Salary + goal planning", "Charts & CSV export"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-[var(--success-green)] mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="mt-8 block bg-black text-white text-center py-3 font-display font-bold tracking-wider hover:bg-[var(--alert-red)] transition-colors">
                CREATE ACCOUNT →
              </Link>
            </div>

            <div className="bg-[var(--alert-red)] text-white p-8 border-2 border-[var(--alert-red)] relative">
              <div className="absolute -top-3 left-6 bg-black text-white font-display font-bold text-xs px-3 py-1 tracking-widest">
                RECOMMENDED
              </div>
              <div className="absolute -top-3 right-6 bg-white text-[var(--alert-red)] font-display font-black text-[10px] px-3 py-1 tracking-widest border-2 border-black">
                SPECIAL LAUNCH PRICE
              </div>
              <div className="overline text-white flex items-center gap-2"><Crown size={12} /> PREMIUM (LIFETIME)</div>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <div className="font-mono text-2xl line-through opacity-60">₹499</div>
                <div className="font-display font-black text-6xl tracking-tighter">₹200</div>
              </div>
              <div className="font-mono text-xs mt-1 opacity-90">One-time · No renewals ever · <span className="font-bold">Save ₹299</span></div>
              <ul className="mt-6 space-y-2 font-mono text-sm">
                {["Everything in Free", "Financial Wellness Score (0–100)", "Financial Stress Indicator with monthly trend", "Unlimited AI Coach chat", "AI monthly insight cards", "\"Why your score moved\" AI note each month", "All future AI features included"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-white mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Link to="/register" className="mt-8 block bg-white text-black text-center py-3 font-display font-bold tracking-wider hover:bg-black hover:text-white transition-colors">
                UPGRADE FOR ₹200 →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-black bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <div className="overline mb-3">FAQ</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Questions people ask.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {[
              { q: "Is my financial data safe?", a: "Yes. Your data lives in a private MongoDB database with JWT-based authentication and bcrypt password hashing. We never sell or share your data." },
              { q: "What does the ₹200 unlock?", a: "The Financial Wellness Score, Financial Stress Indicator, and AI Financial Coach (Claude Sonnet 4.5) with monthly insight cards. Free tier keeps expense tracking, budgets, red-flag alerts, salary planning, and goals — forever." },
              { q: "Do I get charged monthly?", a: "No subscriptions. ₹200 is a one-time payment. You keep AI Coach and all premium features for the lifetime of the account." },
              { q: "Which currencies are supported?", a: "INR, USD, EUR, GBP, JPY, AUD, CAD, SGD, AED, BRL. You can switch anytime from Settings." },
              { q: "Can I export my data?", a: "Yes. One-click CSV export of your full expense history, filterable by month. Great for tax season." },
              { q: "How is this different from other apps?", a: "Most trackers show numbers passively. RED.FLAG scores your wellness, tracks your stress, and has an AI coach that reads the custom data you provide to explain exactly why your score changed." },
            ].map((item) => (
              <div key={item.q} className="border-2 border-black p-5">
                <div className="font-display font-bold text-lg mb-2">{item.q}</div>
                <div className="font-mono text-sm text-[var(--text-secondary)] leading-relaxed">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b-2 border-black bg-[var(--alert-red)] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter">
            Stop guessing.<br />Start tracking.
          </h2>
          <p className="mt-6 font-mono text-base max-w-xl mx-auto">Free to start. Two hundred rupees to unlock the coach. Lifetime, no strings.</p>
          <Link to="/register" data-testid="footer-cta"
            className="mt-10 inline-block bg-black text-white px-10 py-4 font-display font-bold tracking-wider hover:bg-white hover:text-black transition-colors">
            CREATE FREE ACCOUNT →
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-[var(--alert-red)] flex items-center justify-center text-white font-display font-black text-xs">!</div>
            <div className="font-display font-black text-sm tracking-tighter">RED.FLAG</div>
          </div>
          <div className="overline">© 2026 RED.FLAG / KEEP IT IN CHECK</div>
          <div className="flex gap-4 font-mono text-sm">
            <Link to="/login" className="underline">Sign in</Link>
            <Link to="/register" className="underline">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
